import { useCallback, useEffect, useId, useState } from 'react';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { CONCERN_CODES } from '../../lib/constants';
import { errorText, rpc, type AppError } from '../admin/api';
import { HeadFrame, type HeadHomeData } from './HeadPages';

// Release 4c (0022): the "Something else" follow-up on the swipe check-in
// is a closed vocabulary curated per school grade, by that school's head of
// school (or a deputy scoped to that grade) — moved here from the
// platform-admin, per-national-grade panel 4b shipped (R2Admin.tsx's old
// GradeConcernPhrasesPanel, now retired). "I'd rather not say" can never
// be removed, so its checkbox is always on and disabled.
//
// Only a primary head gets the school's grade list from head_home()
// (HeadHomeData['grades'] — the same field the Welfare report's grade
// picker already relies on being primary-only); a deputy's own
// scope_grade_ids never reach the client today, so a scoped deputy is
// pointed at their primary head instead of getting a picker with no
// grades to put in it.
export function HeadSettingsPage() {
  const { t } = useI18n();
  return (
    <HeadFrame tab="settings" title={t['r4c.hse.title']}>
      {(_ctx, home) => <SomethingElsePanel home={home} />}
    </HeadFrame>
  );
}

type GradeCodes = { grade_id: string; label: string; national_level: string; codes: string[] };

function SomethingElsePanel({ home }: { home: HeadHomeData }) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const grades = home.grades;
  const [gradeId, setGradeId] = useState(grades?.[0]?.id ?? '');
  const [data, setData] = useState<GradeCodes | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<AppError | null>(null);

  const load = useCallback(async (g: string) => {
    if (!g) return;
    setErr(null);
    const r = await rpc<GradeCodes>('head_something_else', { p_grade_id: g });
    if (r.error) return setErr(r.error);
    setData(r.data);
    setCodes(r.data.codes);
  }, []);
  useEffect(() => {
    load(gradeId);
  }, [gradeId, load]);

  function toggle(code: string) {
    if (code === 'unspecified') return;
    setCodes((c) => (c.includes(code) ? c.filter((x) => x !== code) : [...c, code]));
  }

  async function save() {
    setBusy(true);
    setErr(null);
    const r = await rpc<{ codes: string[] }>('head_set_something_else', { p_grade_id: gradeId, p_codes: codes });
    setBusy(false);
    if (r.error) return setErr(r.error);
    setCodes(r.data.codes);
    announce(t['r4c.hse.saved']);
  }

  if (!grades || grades.length === 0) {
    return (
      <section className="panel p5" aria-labelledby={`${id}-t`}>
        <h2 className="panel-title" id={`${id}-t`}>{t['r4c.hse.title']}</h2>
        <p className="form-hint">{t['r4c.hse.noGrades']}</p>
      </section>
    );
  }

  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>{t['r4c.hse.title']}</h2>
      <p className="form-hint">{t['r4c.hse.hint']} <Pill kind="warn">{t['ts.draft']}</Pill> {t['r4c.hse.draft']}</p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      <div className="field-group">
        <label className="field-label" htmlFor={`${id}-grade`}>{t['r4c.hse.grade']}</label>
        <select id={`${id}-grade`} className="select" value={gradeId} onChange={(e) => setGradeId(e.target.value)}>
          {grades.map((g) => (
            <option key={g.id} value={g.id}>{t['h2.grade'](g.label)}</option>
          ))}
        </select>
      </div>
      {data && (
        <ul className="sched-list">
          {CONCERN_CODES.map((c) => {
            const locked = c.value === 'unspecified';
            return (
              <li key={c.value}>
                <label className="check">
                  <input type="checkbox" checked={codes.includes(c.value)} disabled={locked} onChange={() => toggle(c.value)} />
                  <span aria-hidden="true">{c.emoji} </span>
                  <span>{t[`concern.${c.value}` as 'concern.unspecified']}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !data}>{t['pa.save']}</button>
      </div>
    </section>
  );
}
